import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import Modal from '@/components/shared/Modal';
import { apiFetch } from '@/lib/api';
import { formatDate, formatCurrency, formatConsumption } from '@/utils/formatters';
import {
  FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiAlertCircle, FiRefreshCw,
  FiAward, FiCheckCircle, FiClock, FiXCircle, FiFileText, FiSave,
  FiShare2, FiLink, FiCopy, FiEye, FiEyeOff, FiInfo,
} from 'react-icons/fi';

/**
 * Detailseite einer Ausschreibung (Route /app/abschluesse/:id).
 *
 * Die Seite laedt ihre Daten selbst per GET /api/ausschreibungen/:id (inklusive
 * der Versorger-Rueckläufe) — bewusst OHNE den Listen-Hook, damit die Liste und
 * die Detailansicht sich nicht gegenseitig ueberschreiben.
 *
 * Fehlerbehandlung folgt der Projektregel:
 *   loadError  => die Ausschreibung konnte nicht geladen werden, es gibt nichts
 *                 anzuzeigen (Ganzseiten-Hinweis mit "Erneut versuchen").
 *   writeError => die angezeigten Daten stimmen weiterhin, nur eine Aenderung
 *                 wurde nicht gespeichert (Banner UEBER dem sichtbaren Inhalt).
 * Ein fehlgeschlagenes Speichern leert also niemals die Seite, und ein offener
 * Dialog bleibt mitsamt den Eingaben stehen.
 */

// ---------------------------------------------------------------------------
// Helfer
// ---------------------------------------------------------------------------

/** Postgres liefert NUMERIC als String ("9.2000"); leere Werte bleiben null. */
const toNum = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(String(value).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

const pad = (n) => String(n).padStart(2, '0');

/**
 * Beliebigen Datumswert auf einen reinen Kalendertag (YYYY-MM-DD) bringen.
 * Ein DATE-Feld ist Mitternacht in der Server-Zeitzone; nach UTC umgerechnet
 * kippt es je nach Offset auf den Vor- oder Folgetag — deshalb zurueckrunden.
 */
const toDateOnly = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const utc = new Date(d.getTime());
  if (utc.getUTCHours() >= 12) utc.setUTCDate(utc.getUTCDate() + 1);
  return utc.toISOString().slice(0, 10);
};

/** Wert fuer ein <input type="date"> — nie undefined, sonst wird das Feld unkontrolliert. */
const toDateInput = (value) => toDateOnly(value) || '';

/** Zahl fuer ein <input type="number"> — "9.2000" wird zu "9.2". */
const toNumInput = (value) => {
  const n = toNum(value);
  return n === null ? '' : String(n);
};

/** Datum + Laufzeit in Monaten => letzter Vertragstag (Beginn + n Monate - 1 Tag). */
const addMonths = (isoDate, months) => {
  const day = toDateOnly(isoDate);
  const m = toNum(months);
  if (!day || m === null) return '';
  const [y, mo, d] = day.split('-').map(Number);
  const end = new Date(Date.UTC(y, mo - 1 + Math.round(m), d));
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
};

const fmtNum = (value, min = 2, max = 4) => {
  const n = toNum(value);
  if (n === null) return '–';
  return n.toLocaleString('de-DE', { minimumFractionDigits: min, maximumFractionDigits: max });
};

const fmtEur = (value) => {
  const n = toNum(value);
  return n === null ? '–' : formatCurrency(n);
};

const fmtKwh = (value) => {
  const n = toNum(value);
  return n === null ? '–' : formatConsumption(n);
};

const fmtText = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return '–';
  return String(value);
};

/** Ganze Zahl (Monate, Anzahl Abnahmestellen). Fehlt der Wert, bleibt es null. */
const toInt = (value) => {
  const n = toNum(value);
  return n === null ? null : Math.round(n);
};

/** Leerer String darf nie an eine Textspalte — daraus wird null. */
const trimOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
};

// ---------------------------------------------------------------------------
// Bindefrist MIT Uhrzeit
// Versorgerpreise verfallen stundengenau. Ein Preis, der um 16:00 laeuft, ist um
// 17:00 abgelaufen — am selben Kalendertag. Ein reiner Datumsvergleich haette
// ihn bis Mitternacht als gueltig gezeigt.
// ---------------------------------------------------------------------------

/** 'HH:MM' normalisieren; alles Unbrauchbare wird null (keine Fantasiezeit). */
const toUhrzeit = (value) => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (!s) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return `${pad(h)}:${pad(min)}`;
};

/**
 * Verfallszeitpunkt einer Bindefrist als lokales Date.
 * OHNE Uhrzeit gilt der Preis bis zum Ende des Kalendertages — sonst waere ein
 * Angebot mit Bindefrist "heute" schon um 00:01 abgelaufen.
 */
const bindefristEnde = (bindefrist, uhrzeit) => {
  const tag = toDateOnly(bindefrist);
  if (!tag) return null;
  const [y, mo, d] = tag.split('-').map(Number);
  const zeit = toUhrzeit(uhrzeit);
  if (!zeit) return new Date(y, mo - 1, d, 23, 59, 59, 999);
  const [hh, mm] = zeit.split(':').map(Number);
  return new Date(y, mo - 1, d, hh, mm, 0, 0);
};

/** Ist die Bindefrist zum Zeitpunkt `jetzt` bereits verstrichen? */
const istFristAbgelaufen = (bindefrist, uhrzeit, jetzt) => {
  const ende = bindefristEnde(bindefrist, uhrzeit);
  if (!ende) return false;
  const ref = jetzt instanceof Date ? jetzt.getTime() : Date.now();
  return ende.getTime() < ref;
};

/** "20.08.2026 um 16:00 Uhr" — ohne Uhrzeit nur das Datum. */
const fmtBindefrist = (bindefrist, uhrzeit) => {
  const tag = toDateOnly(bindefrist);
  if (!tag) return '–';
  const zeit = toUhrzeit(uhrzeit);
  return zeit ? `${formatDate(tag)} um ${zeit} Uhr` : formatDate(tag);
};

// ---------------------------------------------------------------------------
// Tarifinformationen (JSONB-Feld `tarifinfo`)
// An diesen Punkten scheitert ein Abschluss — nicht am Preis. Deshalb werden sie
// strukturiert erfasst statt als Freitext.
// ---------------------------------------------------------------------------

const PREISTYP_OPTIONEN = [
  { value: '', label: 'keine Angabe' },
  { value: 'mischpreis', label: 'Mischpreis — ein Energiepreis ueber die ganze Laufzeit' },
  { value: 'kalenderjahrespreis', label: 'Kalenderjahrespreis — je Kalenderjahr ein eigener Preis' },
];

const PREISTYP_KURZ = {
  mischpreis: 'Mischpreis',
  kalenderjahrespreis: 'Kalenderjahrespreis',
};

/** Postgres liefert JSONB als Objekt; aeltere Zeilen koennen Text enthalten. */
const parseTarifinfo = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return Array.isArray(value) ? {} : value;
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

/** Kurzfassung fuer die Tabelle — nur belegte Felder, nichts Erfundenes. */
const tarifinfoZeilen = (raw) => {
  const t = parseTarifinfo(raw);
  const zeilen = [];
  if (PREISTYP_KURZ[t.preistyp]) zeilen.push(PREISTYP_KURZ[t.preistyp]);
  if (toInt(t.mindestlaufzeit_monate) !== null) zeilen.push(`Mindestlaufzeit ${toInt(t.mindestlaufzeit_monate)} Mon.`);
  if (t.lieferende_fest === true) zeilen.push('festes Lieferende');
  if (t.lieferende_fest === false) zeilen.push('kein festes Lieferende');
  if (trimOrNull(t.mengenflexibilitaet)) zeilen.push(`Menge: ${trimOrNull(t.mengenflexibilitaet)}`);
  if (trimOrNull(t.abrechnung)) zeilen.push(`Abrechnung: ${trimOrNull(t.abrechnung)}`);
  if (trimOrNull(t.bonitaet)) zeilen.push(`Bonitaet: ${trimOrNull(t.bonitaet)}`);
  if (toInt(t.buendelung_max_abnahmestellen) !== null) {
    zeilen.push(`max. ${toInt(t.buendelung_max_abnahmestellen)} Abnahmestellen buendelbar`);
  }
  if (trimOrNull(t.sonstiges)) zeilen.push(trimOrNull(t.sonstiges));
  return zeilen;
};

// ---------------------------------------------------------------------------
// All-in-Arbeitspreis — projektweit verbindliche Formel
//   arbeitspreis = angebot.arbeitspreis_ct_kwh
//                  ?? (energiepreis + netzentgelt + abgaben
//                      + (provision_ct_kwh ?? ziel_provision_ct_kwh ?? 0))
// Fehlt ein Baustein, ist das Ergebnis null (Anzeige "–") — niemals 0. `fehlt`
// benennt die Luecke, damit die Oberflaeche sie begruenden kann.
// ---------------------------------------------------------------------------

const berechneArbeitspreis = (angebot, aus) => {
  const erfasst = toNum(angebot?.arbeitspreis_ct_kwh);
  if (erfasst !== null) return { wert: erfasst, quelle: 'erfasst', fehlt: [] };

  const energie = toNum(angebot?.energiepreis_ct_kwh);
  const netz = toNum(aus?.netzentgelt_ct_kwh);
  const abgaben = toNum(aus?.abgaben_ct_kwh);
  const provision = toNum(aus?.provision_ct_kwh) ?? toNum(aus?.ziel_provision_ct_kwh) ?? 0;

  const fehlt = [];
  if (energie === null) fehlt.push('Energiepreis des Angebots');
  if (netz === null) fehlt.push('Netzentgelt der Ausschreibung');
  if (abgaben === null) fehlt.push('Abgaben und Umlagen der Ausschreibung');
  if (fehlt.length > 0) return { wert: null, quelle: 'berechnet', fehlt };

  return { wert: energie + netz + abgaben + provision, quelle: 'berechnet', fehlt: [] };
};

/** Antwort lesen und Serverfehler als sprechende Exception werfen (Regel: resp.ok pruefen). */
const readJson = async (resp) => {
  let data = null;
  try { data = await resp.json(); } catch { /* leerer Body */ }
  if (!resp.ok) throw new Error((data && data.error) || `Serverfehler (HTTP ${resp.status})`);
  return data;
};

// ---------------------------------------------------------------------------
// Statusdarstellung
// ---------------------------------------------------------------------------

const AUSSCHREIBUNG_STATUS = {
  offen: { label: 'Offen', color: 'bg-blue-100 text-blue-800' },
  angebote_da: { label: 'Angebote da', color: 'bg-amber-100 text-amber-800' },
  abgeschlossen: { label: 'Abgeschlossen', color: 'bg-emerald-100 text-emerald-800' },
  verloren: { label: 'Verloren', color: 'bg-red-100 text-red-800' },
};

const ANGEBOT_STATUS = {
  angefragt: { label: 'Angefragt', color: 'bg-gray-100 text-gray-700' },
  angeboten: { label: 'Angeboten', color: 'bg-emerald-100 text-emerald-800' },
  abgelehnt: { label: 'Abgelehnt', color: 'bg-red-100 text-red-800' },
  abgelaufen: { label: 'Abgelaufen', color: 'bg-gray-100 text-gray-600' },
};

const PROVISION_STATUS = {
  erwartet: { label: 'Erwartet', color: 'bg-amber-100 text-amber-800' },
  bestaetigt: { label: 'Bestaetigt', color: 'bg-blue-100 text-blue-800' },
  ausgezahlt: { label: 'Ausgezahlt', color: 'bg-emerald-100 text-emerald-800' },
};

const Pill = ({ status, map, className = '' }) => {
  const config = map[status] || { label: status || '–', color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${config.color} ${className}`}>
      {config.label}
    </span>
  );
};

const MEDIUM_LABELS = { strom: 'Strom', gas: 'Gas' };

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

/**
 * Die geteilte Button-Komponente faerbt ihre Varianten ueber `bg-primary-600`
 * bzw. `bg-energy-green`. Diese Klassen erzeugen im Tailwind-4-Theme dieses
 * Projekts KEIN CSS mehr — dort sind nur `--color-primary` und `--color-success`
 * definiert, keine Zahlenskala. Die Flaeche bliebe also transparent und die
 * weisse Schrift unlesbar. Bis das Theme zentral um die Skala ergaenzt wird,
 * setzen wir hier die tatsaechlich vorhandenen Token-Klassen nach.
 */
const BTN_PRIMARY = 'bg-primary text-primary-foreground hover:bg-teal';
const BTN_SUCCESS = 'bg-success text-white hover:bg-emerald-600';

const FormError = ({ message }) => {
  if (!message) return null;
  return (
    <div className="flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
      <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
      <div>
        <p className="text-sm font-semibold text-red-700">Nicht gespeichert</p>
        <p className="text-sm text-red-700/90 mt-0.5">{message}</p>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-900 mt-0.5 break-words">{children}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Provisionsrechnung — eine einzige Stelle fuer die Formel
//   provision_jahr_eur   = provision_ct_kwh / 100 * verbrauch_kwh
//   provision_gesamt_eur = provision_jahr_eur * (laufzeit_monate / 12)
// Fehlt ein Wert, ist das Ergebnis null (nicht 0) — sonst suggeriert die
// Oberflaeche eine Provision von 0 EUR, wo schlicht eine Angabe fehlt.
// ---------------------------------------------------------------------------

const berechneProvision = (provisionCtKwh, verbrauchKwh, laufzeitMonate) => {
  const ct = toNum(provisionCtKwh);
  const kwh = toNum(verbrauchKwh);
  const monate = toNum(laufzeitMonate);
  const jahr = ct === null || kwh === null ? null : (ct / 100) * kwh;
  const gesamt = jahr === null || monate === null ? null : jahr * (monate / 12);
  return { jahr, gesamt };
};

// ---------------------------------------------------------------------------
// Formular: Versorger anfragen / Ruecklauf erfassen
// Auf Modulebene definiert, damit React es beim Tippen nicht neu mountet
// (sonst verliert jedes Feld nach einem Zeichen den Fokus).
// ---------------------------------------------------------------------------

const AngebotForm = ({
  mode,              // 'anfrage' | 'ruecklauf'
  initialData,
  ausschreibung,     // fuer die Vorschau des berechneten All-in-Arbeitspreises
  suppliers = [],
  suppliersError = '',
  onSave,
  onCancel,
  saving = false,
  error = '',
}) => {
  const [form, setForm] = useState(() => ({
    supplier_id: initialData?.supplier_id != null ? String(initialData.supplier_id) : '',
    versorger_name: initialData?.versorger_name || '',
    produktname: initialData?.produktname || '',
    status: initialData?.status || 'angefragt',
    laufzeit_monate: toNumInput(initialData?.laufzeit_monate),
    energiepreis_ct_kwh: toNumInput(initialData?.energiepreis_ct_kwh),
    arbeitspreis_ct_kwh: toNumInput(initialData?.arbeitspreis_ct_kwh),
    grundpreis_eur_jahr: toNumInput(initialData?.grundpreis_eur_jahr),
    leistungspreis_eur_kw_jahr: toNumInput(initialData?.leistungspreis_eur_kw_jahr),
    bindefrist: toDateInput(initialData?.bindefrist),
    bindefrist_uhrzeit: toUhrzeit(initialData?.bindefrist_uhrzeit) || '',
    bedingungen: initialData?.bedingungen || '',
  }));

  // Tarifinformationen liegen als verschachteltes JSONB-Objekt vor, werden im
  // Formular aber flach gepflegt und beim Speichern wieder verschachtelt.
  const [tarif, setTarif] = useState(() => {
    const t = parseTarifinfo(initialData?.tarifinfo);
    return {
      preistyp: PREISTYP_KURZ[t.preistyp] ? t.preistyp : '',
      mindestlaufzeit_monate: toNumInput(t.mindestlaufzeit_monate),
      lieferende_fest: t.lieferende_fest === true ? 'ja' : t.lieferende_fest === false ? 'nein' : '',
      mengenflexibilitaet: t.mengenflexibilitaet || '',
      abrechnung: t.abrechnung || '',
      bonitaet: t.bonitaet || '',
      buendelung_max_abnahmestellen: toNumInput(t.buendelung_max_abnahmestellen),
      sonstiges: t.sonstiges || '',
    };
  });

  // Freitext, wenn kein Stammdaten-Versorger hinterlegt ist oder gar keine
  // Stammdaten geladen werden konnten — die Anfrage darf daran nicht scheitern.
  const [freitext, setFreitext] = useState(
    () => initialData?.supplier_id == null || suppliers.length === 0 || !!suppliersError
  );
  const [localError, setLocalError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setT = (key) => (e) => setTarif((t) => ({ ...t, [key]: e.target.value }));

  const handleSupplierChange = (e) => {
    const value = e.target.value;
    const supplier = suppliers.find((s) => String(s.id) === String(value));
    setForm((f) => ({ ...f, supplier_id: value, versorger_name: supplier?.name || f.versorger_name }));
  };

  /**
   * Die flachen Formularfelder wieder zum verschachtelten tarifinfo-Objekt
   * zusammensetzen. Ist kein einziges Feld belegt, wird bewusst null gesendet —
   * so bleibt kein Objekt aus lauter Nullwerten in der Datenbank stehen.
   */
  const baueTarifinfo = () => {
    const t = {
      preistyp: PREISTYP_KURZ[tarif.preistyp] ? tarif.preistyp : null,
      mindestlaufzeit_monate: toInt(tarif.mindestlaufzeit_monate),
      lieferende_fest: tarif.lieferende_fest === 'ja' ? true : tarif.lieferende_fest === 'nein' ? false : null,
      mengenflexibilitaet: trimOrNull(tarif.mengenflexibilitaet),
      abrechnung: trimOrNull(tarif.abrechnung),
      bonitaet: trimOrNull(tarif.bonitaet),
      buendelung_max_abnahmestellen: toInt(tarif.buendelung_max_abnahmestellen),
      sonstiges: trimOrNull(tarif.sonstiges),
    };
    return Object.values(t).every((v) => v === null) ? null : t;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = form.versorger_name.trim();
    if (!freitext && !form.supplier_id) {
      setLocalError('Bitte einen Versorger auswaehlen oder auf Freitext umschalten.');
      return;
    }
    if (!name) {
      setLocalError('Der Versorgername ist ein Pflichtfeld.');
      return;
    }
    if (form.status === 'angeboten' && toNum(form.energiepreis_ct_kwh) === null) {
      setLocalError('Fuer den Status "Angeboten" wird ein Energiepreis benoetigt — ohne ihn ist kein Vergleich moeglich.');
      return;
    }
    if (form.bindefrist_uhrzeit.trim() && toUhrzeit(form.bindefrist_uhrzeit) === null) {
      setLocalError('Die Uhrzeit der Bindefrist muss im Format HH:MM angegeben werden, z.B. 16:00.');
      return;
    }
    // Eine Uhrzeit ohne Datum ist nicht auswertbar — lieber jetzt nachfragen,
    // als sie beim Speichern stillschweigend zu verwerfen.
    if (form.bindefrist_uhrzeit.trim() && !form.bindefrist) {
      setLocalError('Zur Uhrzeit fehlt das Datum der Bindefrist. Bitte das Datum ergaenzen oder die Uhrzeit leeren.');
      return;
    }
    setLocalError('');

    const basis = {
      // Beim Bearbeiten die bestehende Zuordnung behalten. Sonst wuerde ein
      // Ausfall der Versorger-Stammdaten den Freitext-Modus erzwingen und die
      // vorhandene supplier_id stillschweigend loeschen.
      supplier_id: freitext ? (initialData?.supplier_id ?? null) : toNum(form.supplier_id),
      versorger_name: name,
      status: form.status,
      laufzeit_monate: toNum(form.laufzeit_monate),
      energiepreis_ct_kwh: toNum(form.energiepreis_ct_kwh),
      grundpreis_eur_jahr: toNum(form.grundpreis_eur_jahr),
      leistungspreis_eur_kw_jahr: toNum(form.leistungspreis_eur_kw_jahr),
      bindefrist: form.bindefrist || null,
      bedingungen: trimOrNull(form.bedingungen),
    };

    if (mode !== 'ruecklauf') {
      // Reine Anfrage: die Ruecklauf-Felder gar nicht erst senden, damit der
      // Server sie nicht auf null setzt.
      onSave(basis);
      return;
    }

    onSave({
      ...basis,
      produktname: trimOrNull(form.produktname),
      arbeitspreis_ct_kwh: toNum(form.arbeitspreis_ct_kwh),
      bindefrist_uhrzeit: form.bindefrist ? toUhrzeit(form.bindefrist_uhrzeit) : null,
      tarifinfo: baueTarifinfo(),
    });
  };

  const shownError = error || localError;
  const bindefristAbgelaufen = istFristAbgelaufen(form.bindefrist, form.bindefrist_uhrzeit);

  // Vorschau: was traegt die Vergleichsliste ein, wenn der Arbeitspreis leer bleibt?
  const arbeitspreisVorschau = berechneArbeitspreis(
    { arbeitspreis_ct_kwh: null, energiepreis_ct_kwh: form.energiepreis_ct_kwh },
    ausschreibung
  );
  const arbeitspreisErfasst = toNum(form.arbeitspreis_ct_kwh);
  const arbeitspreisUnterEnergie =
    arbeitspreisErfasst !== null
    && toNum(form.energiepreis_ct_kwh) !== null
    && arbeitspreisErfasst < toNum(form.energiepreis_ct_kwh);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormError message={shownError} />

      {/* Versorger */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className={`${labelClass} mb-0`}>Versorger *</label>
          <button
            type="button"
            onClick={() => setFreitext((v) => !v)}
            className="text-xs font-medium text-primary hover:underline"
          >
            {freitext ? 'Aus Stammdaten waehlen' : 'Freitext eingeben'}
          </button>
        </div>
        {freitext ? (
          <input
            value={form.versorger_name}
            onChange={set('versorger_name')}
            className={inputClass}
            placeholder="z.B. Stadtwerke Musterstadt"
            autoFocus
          />
        ) : (
          <select value={form.supplier_id} onChange={handleSupplierChange} className={inputClass}>
            <option value="">– bitte waehlen –</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
        {suppliersError && (
          <p className="text-xs text-amber-700 mt-1">
            Stammdaten nicht verfuegbar ({suppliersError}) — bitte den Versorger als Freitext erfassen.
          </p>
        )}
        {!suppliersError && !freitext && suppliers.length === 0 && (
          <p className="text-xs text-gray-500 mt-1">Es ist noch kein Versorger in den Stammdaten angelegt.</p>
        )}
      </div>

      {mode === 'ruecklauf' && (
        <div>
          <label className={labelClass}>Produktname / Tarifbezeichnung</label>
          <input
            value={form.produktname}
            onChange={set('produktname')}
            className={inputClass}
            placeholder="z.B. Fixstrom Business 24"
          />
          <p className="text-xs text-gray-500 mt-1">
            Wird dem Kunden erst zusammen mit dem Versorgernamen gezeigt — solange die
            Ausschreibung anonymisiert ist, bleibt auch das Produkt verdeckt.
          </p>
        </div>
      )}

      {mode === 'ruecklauf' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Status</label>
            <select value={form.status} onChange={set('status')} className={inputClass}>
              {Object.entries(ANGEBOT_STATUS).map(([value, cfg]) => (
                <option key={value} value={value}>{cfg.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Bindefrist (Datum)</label>
            <input type="date" value={form.bindefrist} onChange={set('bindefrist')} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Bindefrist (Uhrzeit)</label>
            <input
              type="time"
              value={form.bindefrist_uhrzeit}
              onChange={set('bindefrist_uhrzeit')}
              className={inputClass}
              placeholder="16:00"
            />
          </div>
          <div className="md:col-span-3 -mt-2">
            {bindefristAbgelaufen ? (
              <p className="text-xs text-red-600">
                Diese Bindefrist ist bereits verstrichen ({fmtBindefrist(form.bindefrist, form.bindefrist_uhrzeit)})
                — der Preis muss vor einem Abschluss neu bestaetigt werden.
              </p>
            ) : form.bindefrist && !toUhrzeit(form.bindefrist_uhrzeit) ? (
              <p className="text-xs text-amber-700">
                Ohne Uhrzeit gilt der Preis bis zum Ende des Tages. Versorgerpreise verfallen
                aber stundengenau — bitte die Uhrzeit nachtragen, sobald sie bekannt ist.
              </p>
            ) : (
              <p className="text-xs text-gray-500">
                Die Uhrzeit gehoert zur Frist: ein Preis, der um 16:00 laeuft, ist um 17:00
                desselben Tages abgelaufen.
              </p>
            )}
          </div>
        </div>
      )}

      <div className={mode === 'ruecklauf' ? 'grid grid-cols-2 gap-4' : ''}>
        <div>
          <label className={labelClass}>Laufzeit (Monate)</label>
          <input
            type="number"
            min="1"
            step="1"
            value={form.laufzeit_monate}
            onChange={set('laufzeit_monate')}
            className={inputClass}
            placeholder="12"
          />
        </div>
        {mode === 'ruecklauf' && (
          <div>
            <label className={labelClass}>
              Energiepreis (ct/kWh){form.status === 'angeboten' ? ' *' : ''}
            </label>
            <input
              type="number"
              step="0.0001"
              value={form.energiepreis_ct_kwh}
              onChange={set('energiepreis_ct_kwh')}
              className={inputClass}
              placeholder="12.3400"
            />
          </div>
        )}
      </div>

      {mode === 'ruecklauf' && (
        <div>
          <label className={labelClass}>Arbeitspreis all-in (ct/kWh) — optional</label>
          <input
            type="number"
            step="0.0001"
            value={form.arbeitspreis_ct_kwh}
            onChange={set('arbeitspreis_ct_kwh')}
            className={inputClass}
            placeholder={arbeitspreisVorschau.wert === null ? 'wird berechnet' : fmtNum(arbeitspreisVorschau.wert)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Der Arbeitspreis ist der All-in-Preis, den der Kunde vergleicht: Energie + Netzentgelt
            + Abgaben und Umlagen + Provision. <strong>Feld leer lassen</strong>, wenn der Versorger
            nur den Energiepreis genannt hat — dann wird er aus diesen Bausteinen berechnet.
            Eintragen nur, wenn der Versorger selbst einen All-in-Preis ausgewiesen hat.
          </p>
          {arbeitspreisErfasst === null && arbeitspreisVorschau.wert !== null && (
            <p className="text-xs text-gray-600 mt-1">
              Berechnet ergaebe sich: <strong>{fmtNum(arbeitspreisVorschau.wert)} ct/kWh</strong>
              {' '}({fmtNum(form.energiepreis_ct_kwh)} Energie + {fmtNum(ausschreibung?.netzentgelt_ct_kwh)} Netz
              {' '}+ {fmtNum(ausschreibung?.abgaben_ct_kwh)} Abgaben
              {' '}+ {fmtNum(toNum(ausschreibung?.provision_ct_kwh) ?? toNum(ausschreibung?.ziel_provision_ct_kwh) ?? 0)} Provision).
            </p>
          )}
          {arbeitspreisErfasst === null && arbeitspreisVorschau.wert === null && (
            <p className="text-xs text-amber-700 mt-1">
              Noch nicht berechenbar — es fehlt: {arbeitspreisVorschau.fehlt.join(', ')}.
              Bis dahin zeigt die Vergleichsliste beim Arbeitspreis "–".
            </p>
          )}
          {arbeitspreisUnterEnergie && (
            <p className="text-xs text-amber-700 mt-1">
              Der eingetragene Arbeitspreis liegt unter dem Energiepreis. Bitte pruefen — der
              All-in-Preis enthaelt den Energiepreis normalerweise vollstaendig.
            </p>
          )}
        </div>
      )}

      {mode === 'ruecklauf' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Grundpreis (EUR/Jahr)</label>
            <input
              type="number"
              step="0.01"
              value={form.grundpreis_eur_jahr}
              onChange={set('grundpreis_eur_jahr')}
              className={inputClass}
              placeholder="120.00"
            />
          </div>
          <div>
            <label className={labelClass}>Leistungspreis (EUR/kW/Jahr)</label>
            <input
              type="number"
              step="0.01"
              value={form.leistungspreis_eur_kw_jahr}
              onChange={set('leistungspreis_eur_kw_jahr')}
              className={inputClass}
              placeholder="65.00"
            />
          </div>
        </div>
      )}

      {/* Tarifinformationen — daran scheitert ein Abschluss, nicht am Preis */}
      {mode === 'ruecklauf' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4 space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Tarifinformationen
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Alle Angaben optional. Sie erscheinen beim Kunden als strukturierte Liste — das
              sind die Punkte, an denen ein Abschluss scheitert, nicht der Preis.
            </p>
          </div>

          <div>
            <label className={labelClass}>Preistyp</label>
            <select value={tarif.preistyp} onChange={setT('preistyp')} className={inputClass}>
              {PREISTYP_OPTIONEN.map((o) => (
                <option key={o.value || 'leer'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Mindestvertragslaufzeit (Monate)</label>
              <input
                type="number"
                min="1"
                step="1"
                value={tarif.mindestlaufzeit_monate}
                onChange={setT('mindestlaufzeit_monate')}
                className={inputClass}
                placeholder="24"
              />
            </div>
            <div>
              <label className={labelClass}>Festes Lieferende</label>
              <select value={tarif.lieferende_fest} onChange={setT('lieferende_fest')} className={inputClass}>
                <option value="">keine Angabe</option>
                <option value="ja">Ja — festes Lieferende (z.B. zum 31.12.)</option>
                <option value="nein">Nein — Lieferende laufzeitabhaengig</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Mengenflexibilitaet</label>
              <input
                value={tarif.mengenflexibilitaet}
                onChange={setT('mengenflexibilitaet')}
                className={inputClass}
                placeholder="z.B. voll gewaehrleistet oder +/- 20 %"
              />
            </div>
            <div>
              <label className={labelClass}>Abrechnung</label>
              <input
                value={tarif.abrechnung}
                onChange={setT('abrechnung')}
                className={inputClass}
                placeholder="z.B. Monatsrechnung + Jahreslastgang"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Bonitaetsanforderung</label>
              <input
                value={tarif.bonitaet}
                onChange={setT('bonitaet')}
                className={inputClass}
                placeholder="z.B. Creditreform-Index < 300"
              />
              <p className="text-xs text-gray-500 mt-1">
                Auch die Verschaerfung ab einer Verbrauchsmenge hier vermerken.
              </p>
            </div>
            <div>
              <label className={labelClass}>Max. buendelbare Abnahmestellen</label>
              <input
                type="number"
                min="1"
                step="1"
                value={tarif.buendelung_max_abnahmestellen}
                onChange={setT('buendelung_max_abnahmestellen')}
                className={inputClass}
                placeholder="10"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Sonstiges</label>
            <textarea
              value={tarif.sonstiges}
              onChange={setT('sonstiges')}
              rows={2}
              className={inputClass}
              placeholder="Weitere Tarifbedingung, die der Kunde kennen muss"
            />
          </div>
        </div>
      )}

      <div>
        <label className={labelClass}>Bemerkung / Bedingungen</label>
        <textarea
          value={form.bedingungen}
          onChange={set('bedingungen')}
          rows={3}
          className={inputClass}
          placeholder={mode === 'anfrage' ? 'z.B. Anfrage per E-Mail an den Vertrieb' : 'z.B. Preis gilt nur bei Abschluss bis Monatsende'}
        />
      </div>

      <div className="flex justify-end space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>Abbrechen</Button>
        <Button type="submit" variant="primary" className={BTN_PRIMARY} icon={FiSave} loading={saving}>
          {saving ? 'Speichere …' : 'Speichern'}
        </Button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Formular: Abschluss erfassen (mit Live-Vorschau der Provision)
// ---------------------------------------------------------------------------

const AbschlussForm = ({ ausschreibung, angebote, onSave, onCancel, saving = false, error = '' }) => {
  const [form, setForm] = useState(() => ({
    gewinner_angebot_id: ausschreibung.gewinner_angebot_id != null ? String(ausschreibung.gewinner_angebot_id) : '',
    vertragsbeginn: toDateInput(ausschreibung.vertragsbeginn) || toDateInput(ausschreibung.lieferbeginn),
    vertragsende: toDateInput(ausschreibung.vertragsende),
    provision_ct_kwh: toNumInput(ausschreibung.provision_ct_kwh ?? ausschreibung.ziel_provision_ct_kwh),
    provision_status: ausschreibung.provision_status || 'erwartet',
    notizen: ausschreibung.notizen || '',
  }));
  const [localError, setLocalError] = useState('');
  // Hat der Nutzer das Vertragsende selbst gesetzt? Dann nicht mehr vorschlagen.
  const [endeManuell, setEndeManuell] = useState(() => !!toDateInput(ausschreibung.vertragsende));

  const set = (key) => (e) => {
    if (key === 'vertragsende') setEndeManuell(true);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const gewinner = angebote.find((a) => String(a.id) === String(form.gewinner_angebot_id)) || null;

  /**
   * Bei der Auswahl des Gewinners das Vertragsende vorschlagen — und den
   * Vorschlag nachfuehren, wenn der Nutzer auf ein Angebot mit anderer Laufzeit
   * wechselt. Sobald er das Feld selbst angefasst hat (endeManuell), bleibt
   * seine Eingabe unangetastet.
   */
  const handleGewinner = (angebot) => {
    setForm((f) => {
      const next = { ...f, gewinner_angebot_id: String(angebot.id) };
      if (!endeManuell && f.vertragsbeginn) {
        const vorschlag = addMonths(f.vertragsbeginn, angebot.laufzeit_monate);
        if (vorschlag) next.vertragsende = vorschlag;
      }
      return next;
    });
  };

  const vorschau = berechneProvision(
    form.provision_ct_kwh,
    ausschreibung.verbrauch_kwh,
    gewinner?.laufzeit_monate
  );

  const fehlendeAngaben = [];
  if (toNum(form.provision_ct_kwh) === null) fehlendeAngaben.push('Provision in ct/kWh');
  if (toNum(ausschreibung.verbrauch_kwh) === null) fehlendeAngaben.push('Verbrauch der Ausschreibung');
  if (toNum(gewinner?.laufzeit_monate) === null) fehlendeAngaben.push('Laufzeit des Gewinner-Angebots');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.gewinner_angebot_id) {
      setLocalError('Bitte das Gewinner-Angebot auswaehlen.');
      return;
    }
    if (form.vertragsbeginn && form.vertragsende && form.vertragsende < form.vertragsbeginn) {
      setLocalError('Das Vertragsende liegt vor dem Vertragsbeginn.');
      return;
    }
    setLocalError('');
    onSave({
      gewinner_angebot_id: toNum(form.gewinner_angebot_id),
      vertragsbeginn: form.vertragsbeginn || null,
      vertragsende: form.vertragsende || null,
      provision_ct_kwh: toNum(form.provision_ct_kwh),
      provision_status: form.provision_status,
      notizen: form.notizen.trim() ? form.notizen.trim() : null,
    });
  };

  const shownError = error || localError;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormError message={shownError} />

      {/* Gewinner-Auswahl */}
      <div>
        <label className={labelClass}>Gewinner-Angebot *</label>
        <div className="space-y-2">
          {angebote.map((a) => {
            const selected = String(a.id) === String(form.gewinner_angebot_id);
            // Stundengenau: ein Preis mit Bindefrist "heute 16:00" ist um 17:00 abgelaufen.
            const abgelaufen = istFristAbgelaufen(a.bindefrist, a.bindefrist_uhrzeit);
            return (
              <label
                key={a.id}
                className={`flex items-start space-x-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                  selected ? 'border-primary bg-accent' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="gewinner"
                  className="mt-1"
                  checked={selected}
                  onChange={() => handleGewinner(a)}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-900">
                    {a.versorger_name}
                    {a.produktname ? <span className="font-normal text-gray-500"> · {a.produktname}</span> : null}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {fmtNum(a.energiepreis_ct_kwh)} ct/kWh
                    {' · '}
                    {toNum(a.laufzeit_monate) === null ? 'Laufzeit –' : `${toNum(a.laufzeit_monate)} Monate`}
                    {a.bindefrist ? ` · Bindefrist ${fmtBindefrist(a.bindefrist, a.bindefrist_uhrzeit)}` : ''}
                  </span>
                  {abgelaufen && (
                    <span className="block text-xs text-red-600 mt-0.5">Bindefrist abgelaufen — Preis vor Abschluss bestaetigen lassen.</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Vertragsbeginn</label>
          <input type="date" value={form.vertragsbeginn} onChange={set('vertragsbeginn')} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Vertragsende</label>
          <input type="date" value={form.vertragsende} onChange={set('vertragsende')} className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Provision (ct/kWh)</label>
          <input
            type="number"
            step="0.0001"
            value={form.provision_ct_kwh}
            onChange={set('provision_ct_kwh')}
            className={inputClass}
            placeholder="0.2000"
          />
          {toNum(ausschreibung.ziel_provision_ct_kwh) !== null && (
            <p className="text-xs text-gray-500 mt-1">
              Ziel laut Ausschreibung: {fmtNum(ausschreibung.ziel_provision_ct_kwh)} ct/kWh
            </p>
          )}
        </div>
        <div>
          <label className={labelClass}>Provisionsstatus</label>
          <select value={form.provision_status} onChange={set('provision_status')} className={inputClass}>
            {Object.entries(PROVISION_STATUS).map(([value, cfg]) => (
              <option key={value} value={value}>{cfg.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Live-Vorschau — zeigt vor dem Speichern, worum es geht */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Vorschau Provision</p>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div>
            <p className="text-xs text-gray-500">Pro Jahr</p>
            <p className="text-lg font-bold text-gray-900">{fmtEur(vorschau.jahr)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Gesamt ueber die Laufzeit</p>
            <p className="text-lg font-bold text-primary">{fmtEur(vorschau.gesamt)}</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {fmtKwh(ausschreibung.verbrauch_kwh)} × {fmtNum(form.provision_ct_kwh)} ct/kWh
          {toNum(gewinner?.laufzeit_monate) !== null ? ` × ${toNum(gewinner.laufzeit_monate)}/12 Monate` : ''}
        </p>
        {fehlendeAngaben.length > 0 && (
          <p className="text-xs text-amber-700 mt-1">
            Noch nicht berechenbar — es fehlt: {fehlendeAngaben.join(', ')}.
          </p>
        )}
      </div>

      <div>
        <label className={labelClass}>Notizen</label>
        <textarea value={form.notizen} onChange={set('notizen')} rows={3} className={inputClass} />
      </div>

      <div className="flex justify-end space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>Abbrechen</Button>
        <Button type="submit" variant="success" className={BTN_SUCCESS} icon={FiAward} loading={saving}>
          {saving ? 'Speichere …' : 'Abschluss speichern'}
        </Button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

const AbschlussDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ausschreibung, setAusschreibung] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [writeError, setWriteError] = useState('');
  const [notice, setNotice] = useState('');

  // Versorger-Stammdaten sind eine Bequemlichkeit, keine Voraussetzung:
  // scheitert der Abruf, bleibt die Freitext-Eingabe moeglich.
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersError, setSuppliersError] = useState('');

  // Dialoge — jeder mit eigenem Fehler- und Speicherzustand, damit ein
  // Fehlschlag den Dialog offen und die Eingaben erhalten laesst.
  const [angebotModal, setAngebotModal] = useState(null); // { mode, angebot }
  const [angebotError, setAngebotError] = useState('');
  const [angebotSaving, setAngebotSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [showAbschluss, setShowAbschluss] = useState(false);
  const [abschlussError, setAbschlussError] = useState('');
  const [abschlussSaving, setAbschlussSaving] = useState(false);

  const [showVerloren, setShowVerloren] = useState(false);
  const [verlorenGrund, setVerlorenGrund] = useState('');
  const [verlorenError, setVerlorenError] = useState('');
  const [verlorenSaving, setVerlorenSaving] = useState(false);

  const [provisionSaving, setProvisionSaving] = useState('');
  const [zahldatum, setZahldatum] = useState('');
  const [zahldatumSaving, setZahldatumSaving] = useState(false);

  // Abschnitt "Vergleich mit dem Kunden teilen" — eigener Fehlerzustand, damit
  // ein misslungener Link nicht als allgemeiner Seitenfehler erscheint.
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [kopierStatus, setKopierStatus] = useState(''); // '' | 'ok' | 'manuell'
  const [sichtbarSaving, setSichtbarSaving] = useState(false);
  const linkInputRef = useRef(null);

  /**
   * Bindefristen verfallen stundengenau. Ein einmal beim Rendern berechneter
   * Zeitpunkt wuerde die Warnung erst beim naechsten Seitenwechsel zeigen —
   * deshalb tickt hier eine Minutenuhr.
   */
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setJetzt(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Verhindert setState nach dem Unmount. Das Zuruecksetzen auf true im
  // Effekt-Rumpf ist wichtig: React StrictMode mountet in der Entwicklung
  // zweimal — ohne das bliebe das Flag nach dem simulierten Unmount false und
  // die Seite haenge dauerhaft bei "Lade Ausschreibung …".
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const normalize = (row) => ({ ...row, angebote: Array.isArray(row?.angebote) ? row.angebote : [] });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const resp = await apiFetch(`/api/ausschreibungen/${id}`);
      const data = await readJson(resp);
      if (!data || data.id == null) throw new Error('Die Ausschreibung existiert nicht (mehr).');
      if (!alive.current) return;
      setAusschreibung(normalize(data));
      setZahldatum(toDateInput(data.provision_zahldatum));
    } catch (err) {
      if (!alive.current) return;
      // Kein stiller Fallback: lieber ehrlich leer als falsche Zahlen.
      setLoadError(err.message || 'Ausschreibung konnte nicht geladen werden');
      setAusschreibung(null);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const resp = await apiFetch('/api/suppliers');
        const data = await readJson(resp);
        if (ignore) return;
        setSuppliers(Array.isArray(data) ? data.filter((s) => s.is_active !== false) : []);
        setSuppliersError('');
      } catch (err) {
        if (ignore) return;
        setSuppliers([]);
        setSuppliersError(err.message || 'Versorger konnten nicht geladen werden');
      }
    })();
    return () => { ignore = true; };
  }, []);

  /**
   * Stiller Nachladevorgang nach einer erfolgreichen Aenderung: der Server
   * fuehrt z.B. den Status der Ausschreibung nach. Schlaegt er fehl, bleibt die
   * bereits aktualisierte lokale Sicht stehen — der Schreibvorgang war ja
   * erfolgreich, ein Ladefehler waere hier irrefuehrend.
   */
  const refreshQuiet = useCallback(async () => {
    try {
      const resp = await apiFetch(`/api/ausschreibungen/${id}`);
      const data = await readJson(resp);
      if (data && data.id != null && alive.current) {
        setAusschreibung(normalize(data));
        setZahldatum(toDateInput(data.provision_zahldatum));
      }
    } catch { /* bewusst still */ }
  }, [id]);

  const angebote = useMemo(
    () => (Array.isArray(ausschreibung?.angebote) ? ausschreibung.angebote : []),
    [ausschreibung]
  );

  /** Eingegangene Angebote — nur sie sind vergleichbar und abschlussfaehig. */
  const eingegangen = useMemo(
    () => angebote.filter((a) => a.status === 'angeboten'),
    [angebote]
  );

  /** Guenstigstes eingegangenes Angebot (nur mit Preis — sonst kein Vergleich). */
  const bestesAngebotId = useMemo(() => {
    const mitPreis = eingegangen.filter((a) => toNum(a.energiepreis_ct_kwh) !== null);
    if (mitPreis.length === 0) return null;
    return mitPreis.reduce((best, a) =>
      toNum(a.energiepreis_ct_kwh) < toNum(best.energiepreis_ct_kwh) ? a : best
    ).id;
  }, [eingegangen]);

  // Stundengenau statt tagesgenau: ein Preis mit Bindefrist "heute 16:00" ist um
  // 17:00 abgelaufen, obwohl das Datum noch stimmt.
  const abgelaufeneFristen = useMemo(
    () => angebote.filter(
      (a) => a.status !== 'abgelehnt' && istFristAbgelaufen(a.bindefrist, a.bindefrist_uhrzeit, jetzt)
    ),
    [angebote, jetzt]
  );

  /** Angebotene Rueckläufe ohne Uhrzeit — die Frist ist dort unscharf. */
  const ohneUhrzeit = useMemo(
    () => eingegangen.filter((a) => a.bindefrist && !toUhrzeit(a.bindefrist_uhrzeit)),
    [eingegangen]
  );

  /** Sortierung fuer den Vergleich: eingegangene Angebote nach Preis zuerst. */
  const sortierteAngebote = useMemo(() => {
    const rang = { angeboten: 0, angefragt: 1, abgelaufen: 2, abgelehnt: 3 };
    return [...angebote].sort((a, b) => {
      const ra = rang[a.status] ?? 4;
      const rb = rang[b.status] ?? 4;
      if (ra !== rb) return ra - rb;
      const pa = toNum(a.energiepreis_ct_kwh);
      const pb = toNum(b.energiepreis_ct_kwh);
      if (pa !== null && pb !== null && pa !== pb) return pa - pb;
      if (pa !== null && pb === null) return -1;
      if (pa === null && pb !== null) return 1;
      return (a.versorger_name || '').localeCompare(b.versorger_name || '', 'de');
    });
  }, [angebote]);

  // --- Ladezustaende -------------------------------------------------------

  if (loading) {
    return <p className="text-sm text-gray-400 py-20 text-center">Lade Ausschreibung …</p>;
  }

  // Ganzseiten-Fehler NUR beim Laden. Ein fehlgeschlagener Schreibvorgang
  // darf die geladene Seite niemals ersetzen.
  if (loadError) {
    return (
      <div className="text-center py-20">
        <FiAlertCircle className="mx-auto text-red-400 mb-4" size={48} />
        <h2 className="text-xl font-semibold text-gray-900">Ausschreibung konnte nicht geladen werden</h2>
        <p className="text-sm text-red-600 mt-1">{loadError}</p>
        <div className="flex items-center justify-center space-x-3 mt-4">
          <Button variant="primary" className={BTN_PRIMARY} icon={FiRefreshCw} onClick={load}>Erneut versuchen</Button>
          <Button variant="secondary" onClick={() => navigate('/app/abschluesse')}>Zurueck zur Uebersicht</Button>
        </div>
      </div>
    );
  }

  if (!ausschreibung) {
    return (
      <div className="text-center py-20">
        <FiAlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
        <h2 className="text-xl font-semibold text-gray-900">Ausschreibung nicht gefunden</h2>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/app/abschluesse')}>
          Zurueck zur Uebersicht
        </Button>
      </div>
    );
  }

  const verbrauch = toNum(ausschreibung.verbrauch_kwh);
  const istAbgeschlossen = ausschreibung.status === 'abgeschlossen';
  const istVerloren = ausschreibung.status === 'verloren';
  const gewinner = angebote.find((a) => String(a.id) === String(ausschreibung.gewinner_angebot_id)) || null;

  /** Jahres-Energiekosten eines Angebots. Fehlt ein Wert => "–", nicht 0. */
  const jahresEnergiekosten = (angebot) => {
    const preis = toNum(angebot.energiepreis_ct_kwh);
    if (preis === null || verbrauch === null) return null;
    return (preis / 100) * verbrauch;
  };

  // --- Schreibvorgaenge ----------------------------------------------------

  const closeAngebotModal = () => {
    if (angebotSaving) return;
    setAngebotModal(null);
    // Ein offener Dialogfehler geht nicht verloren, sondern wandert ins Banner.
    if (angebotError) setWriteError(angebotError);
    setAngebotError('');
  };

  const handleAngebotSave = async (payload) => {
    setAngebotSaving(true);
    setAngebotError('');
    const isEdit = !!angebotModal?.angebot;
    try {
      const resp = await apiFetch(
        isEdit
          ? `/api/ausschreibungen/${id}/angebote/${angebotModal.angebot.id}`
          : `/api/ausschreibungen/${id}/angebote`,
        { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(payload) }
      );
      const saved = await readJson(resp);
      if (!saved || saved.id == null) {
        throw new Error('Der Server hat kein gespeichertes Angebot zurueckgegeben. Bitte Seite neu laden.');
      }
      // Erst jetzt — nach der Bestaetigung des Servers — die Ansicht anpassen.
      setAusschreibung((prev) => {
        if (!prev) return prev;
        const list = Array.isArray(prev.angebote) ? prev.angebote : [];
        return {
          ...prev,
          angebote: isEdit
            ? list.map((a) => (String(a.id) === String(saved.id) ? saved : a))
            : [...list, saved],
        };
      });
      setWriteError('');
      setNotice(isEdit ? 'Ruecklauf gespeichert.' : `Anfrage an ${saved.versorger_name} vermerkt.`);
      setAngebotModal(null);
      // Der Server fuehrt ggf. den Status der Ausschreibung nach.
      refreshQuiet();
    } catch (err) {
      // Dialog bleibt offen, Eingaben bleiben erhalten.
      setAngebotError(err.message || 'Angebot konnte nicht gespeichert werden');
    } finally {
      if (alive.current) setAngebotSaving(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    if (deleteError) setWriteError(deleteError);
    setDeleteError('');
  };

  const handleAngebotDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const resp = await apiFetch(`/api/ausschreibungen/${id}/angebote/${deleteTarget.id}`, { method: 'DELETE' });
      await readJson(resp);
      setAusschreibung((prev) => prev ? {
        ...prev,
        angebote: (prev.angebote || []).filter((a) => String(a.id) !== String(deleteTarget.id)),
      } : prev);
      setWriteError('');
      setNotice(`Eintrag "${deleteTarget.versorger_name}" geloescht.`);
      setDeleteTarget(null);
      refreshQuiet();
    } catch (err) {
      setDeleteError(err.message || 'Eintrag konnte nicht geloescht werden');
    } finally {
      if (alive.current) setDeleting(false);
    }
  };

  /** Teil-Update der Ausschreibung; gibt true zurueck, wenn der Server bestaetigt hat. */
  const patchAusschreibung = async (changes) => {
    const resp = await apiFetch(`/api/ausschreibungen/${id}`, {
      method: 'PUT',
      body: JSON.stringify(changes),
    });
    const saved = await readJson(resp);
    if (!saved || saved.id == null) {
      throw new Error('Der Server hat keine gespeicherte Ausschreibung zurueckgegeben. Bitte Seite neu laden.');
    }
    setAusschreibung((prev) => ({
      ...(prev || {}),
      ...saved,
      // Die Rueckläufe kommen bei PUT nicht mit — vorhandene Liste behalten.
      angebote: Array.isArray(saved.angebote) ? saved.angebote : (prev?.angebote || []),
    }));
    return saved;
  };

  // --- Vergleich mit dem Kunden teilen ---------------------------------------
  const vergleichLink = ausschreibung?.vergleich_token
    ? `${window.location.origin}/v/${ausschreibung.vergleich_token}`
    : '';

  const handleLinkErzeugen = async () => {
    setLinkError('');
    setLinkSaving(true);
    try {
      const resp = await apiFetch(`/api/ausschreibungen/${id}/vergleich-link`, { method: 'POST' });
      const data = await readJson(resp);
      if (!data || !data.vergleich_token) {
        throw new Error('Der Server hat keinen Link zurueckgegeben. Bitte erneut versuchen.');
      }
      setAusschreibung((prev) => ({ ...(prev || {}), vergleich_token: data.vergleich_token }));
    } catch (err) {
      setLinkError(err.message || 'Link konnte nicht erzeugt werden');
    } finally {
      setLinkSaving(false);
    }
  };

  const handleLinkKopieren = async () => {
    if (!vergleichLink) return;
    try {
      await navigator.clipboard.writeText(vergleichLink);
      setKopierStatus('ok');
    } catch {
      // Zwischenablage kann blockiert sein — dann Text markieren statt zu behaupten,
      // es sei kopiert worden.
      linkInputRef.current?.select();
      setKopierStatus('manuell');
    }
    setTimeout(() => setKopierStatus(''), 2500);
  };

  const handleSichtbarkeit = async (naechster) => {
    setLinkError('');
    setSichtbarSaving(true);
    try {
      await patchAusschreibung({ versorger_sichtbar: naechster });
    } catch (err) {
      setLinkError(err.message || 'Einstellung konnte nicht gespeichert werden');
    } finally {
      setSichtbarSaving(false);
    }
  };

  const closeAbschlussModal = () => {
    if (abschlussSaving) return;
    setShowAbschluss(false);
    if (abschlussError) setWriteError(abschlussError);
    setAbschlussError('');
  };

  const handleAbschluss = async (payload) => {
    setAbschlussSaving(true);
    setAbschlussError('');
    try {
      const resp = await apiFetch(`/api/ausschreibungen/${id}/abschluss`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const saved = await readJson(resp);
      if (!saved || saved.id == null) {
        throw new Error('Der Server hat keinen gespeicherten Abschluss zurueckgegeben. Bitte Seite neu laden.');
      }
      setAusschreibung((prev) => ({
        ...(prev || {}),
        ...saved,
        angebote: Array.isArray(saved.angebote) ? saved.angebote : (prev?.angebote || []),
      }));
      setZahldatum(toDateInput(saved.provision_zahldatum));
      setWriteError('');
      setNotice('Abschluss gespeichert.');
      setShowAbschluss(false);
    } catch (err) {
      setAbschlussError(err.message || 'Abschluss konnte nicht gespeichert werden');
    } finally {
      if (alive.current) setAbschlussSaving(false);
    }
  };

  const closeVerlorenModal = () => {
    if (verlorenSaving) return;
    setShowVerloren(false);
    if (verlorenError) setWriteError(verlorenError);
    setVerlorenError('');
  };

  const handleVerloren = async (e) => {
    e.preventDefault();
    if (!verlorenGrund.trim()) {
      setVerlorenError('Bitte einen Grund angeben — er wird spaeter fuer die Auswertung gebraucht.');
      return;
    }
    setVerlorenSaving(true);
    setVerlorenError('');
    try {
      await patchAusschreibung({ status: 'verloren', verloren_grund: verlorenGrund.trim() });
      setWriteError('');
      setNotice('Ausschreibung als verloren markiert.');
      setShowVerloren(false);
      setVerlorenGrund('');
    } catch (err) {
      setVerlorenError(err.message || 'Status konnte nicht gespeichert werden');
    } finally {
      if (alive.current) setVerlorenSaving(false);
    }
  };

  const handleWiederOeffnen = async () => {
    setWriteError('');
    try {
      await patchAusschreibung({
        status: eingegangen.length > 0 ? 'angebote_da' : 'offen',
        verloren_grund: null,
      });
      setNotice('Ausschreibung wieder geoeffnet.');
    } catch (err) {
      setWriteError(err.message || 'Status konnte nicht gespeichert werden');
    }
  };

  const handleProvisionStatus = async (status) => {
    if (provisionSaving) return;
    setProvisionSaving(status);
    setWriteError('');
    try {
      await patchAusschreibung({ provision_status: status });
      setNotice(`Provisionsstatus auf "${PROVISION_STATUS[status]?.label || status}" gesetzt.`);
    } catch (err) {
      // Der Inhalt bleibt sichtbar, nur der Fehler kommt als Banner darueber.
      setWriteError(err.message || 'Provisionsstatus konnte nicht gespeichert werden');
    } finally {
      if (alive.current) setProvisionSaving('');
    }
  };

  const handleZahldatum = async () => {
    if (zahldatumSaving) return;
    setZahldatumSaving(true);
    setWriteError('');
    try {
      await patchAusschreibung({ provision_zahldatum: zahldatum || null });
      setNotice('Zahldatum gespeichert.');
    } catch (err) {
      setWriteError(err.message || 'Zahldatum konnte nicht gespeichert werden');
    } finally {
      if (alive.current) setZahldatumSaving(false);
    }
  };

  // --- Darstellung ---------------------------------------------------------

  const laufzeitenText = Array.isArray(ausschreibung.laufzeiten) && ausschreibung.laufzeiten.length > 0
    ? `${ausschreibung.laufzeiten.join(', ')} Monate`
    : '–';

  const provisionAnzeige = berechneProvision(
    ausschreibung.provision_ct_kwh,
    ausschreibung.verbrauch_kwh,
    gewinner?.laufzeit_monate
  );
  // Der Server rechnet die Provision beim Abschluss aus; seine Werte haben
  // Vorrang. Die lokale Rechnung dient nur als Rueckfalloption.
  const provisionJahr = toNum(ausschreibung.provision_jahr_eur) ?? provisionAnzeige.jahr;
  const provisionGesamt = toNum(ausschreibung.provision_gesamt_eur) ?? provisionAnzeige.gesamt;

  return (
    <div className="space-y-6">
      {/* 1. Kopfbereich */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <button
            onClick={() => navigate('/app/abschluesse')}
            className="mt-1 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            title="Zurueck zur Uebersicht"
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              {fmtText(ausschreibung.nummer)}
            </p>
            <h1 className="text-2xl font-bold text-gray-900">{fmtText(ausschreibung.company_name)}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <Pill status={ausschreibung.status} map={AUSSCHREIBUNG_STATUS} />
              {ausschreibung.contact_name && (
                <span className="text-sm text-gray-500">{ausschreibung.contact_name}</span>
              )}
              {ausschreibung.created_by_name && (
                <span className="text-sm text-gray-400">angelegt von {ausschreibung.created_by_name}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {!istVerloren && (
            <Button
              variant="secondary"
              icon={FiXCircle}
              onClick={() => { setVerlorenError(''); setShowVerloren(true); }}
            >
              Als verloren markieren
            </Button>
          )}
          <Button
            variant={istAbgeschlossen ? 'secondary' : 'success'}
            className={istAbgeschlossen ? '' : BTN_SUCCESS}
            icon={FiAward}
            disabled={eingegangen.length === 0}
            title={eingegangen.length === 0 ? 'Es liegt noch kein Angebot mit dem Status "Angeboten" vor.' : undefined}
            onClick={() => { setAbschlussError(''); setShowAbschluss(true); }}
          >
            {istAbgeschlossen ? 'Abschluss anpassen' : 'Abschluss erfassen'}
          </Button>
        </div>
      </div>

      {/* Schreibfehler — der Inhalt darunter bleibt sichtbar */}
      {writeError && (
        <div className="flex items-start space-x-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">Aenderung wurde nicht gespeichert</p>
            <p className="text-sm text-red-700/90 mt-0.5">{writeError}</p>
          </div>
          <button onClick={() => setWriteError('')} className="text-xs font-semibold text-red-700 hover:underline">
            Schliessen
          </button>
        </div>
      )}

      {/* Bestaetigte Erfolgsmeldung (erst nach Server-Antwort) */}
      {notice && (
        <div className="flex items-start space-x-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <FiCheckCircle className="text-emerald-500 flex-shrink-0 mt-0.5" size={18} />
          <p className="flex-1 text-sm text-emerald-800">{notice}</p>
          <button onClick={() => setNotice('')} className="text-xs font-semibold text-emerald-800 hover:underline">
            Schliessen
          </button>
        </div>
      )}

      {/* Abgelaufene Bindefristen — im Energiehandel geschaeftskritisch */}
      {abgelaufeneFristen.length > 0 && !istAbgeschlossen && (
        <div className="flex items-start space-x-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <FiClock className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">
              {abgelaufeneFristen.length === 1 ? 'Eine Bindefrist ist abgelaufen' : `${abgelaufeneFristen.length} Bindefristen sind abgelaufen`}
            </p>
            <p className="text-sm text-amber-800/90 mt-0.5">
              {abgelaufeneFristen.map((a) => `${a.versorger_name} (${formatDate(a.bindefrist)})`).join(', ')}
              {' — '}
              diese Preise sind nicht mehr bindend und muessen vor einem Abschluss neu bestaetigt werden.
            </p>
          </div>
        </div>
      )}

      {/* Eckdaten */}
      <Card>
        <Card.Title>Eckdaten der Ausschreibung</Card.Title>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <Field label="Medium">{MEDIUM_LABELS[ausschreibung.medium] || fmtText(ausschreibung.medium)}</Field>
          <Field label="PLZ">{fmtText(ausschreibung.plz)}</Field>
          <Field label="Verbrauch">{fmtKwh(ausschreibung.verbrauch_kwh)}</Field>
          <Field label="Zaehlverfahren">{fmtText(ausschreibung.zaehlverfahren)}</Field>
          <Field label="Lieferbeginn">{ausschreibung.lieferbeginn ? formatDate(ausschreibung.lieferbeginn) : '–'}</Field>
          <Field label="Angefragte Laufzeiten">{laufzeitenText}</Field>
          <Field label="MaLo-ID">{fmtText(ausschreibung.malo_id)}</Field>
          <Field label="Angebotstyp">{fmtText(ausschreibung.angebotstyp)}</Field>
          <Field label="Hoechstleistung">
            {toNum(ausschreibung.hoechstleistung_kw) === null ? '–' : `${fmtNum(ausschreibung.hoechstleistung_kw, 0, 2)} kW`}
          </Field>
          <Field label="Spannungsebene">{fmtText(ausschreibung.spannungsebene)}</Field>
          <Field label="Ziel-Provision">
            {toNum(ausschreibung.ziel_provision_ct_kwh) === null ? '–' : `${fmtNum(ausschreibung.ziel_provision_ct_kwh)} ct/kWh`}
          </Field>
          <Field label="Angelegt am">{ausschreibung.created_at ? formatDate(ausschreibung.created_at) : '–'}</Field>
        </div>
        {ausschreibung.notizen && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Notizen</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{ausschreibung.notizen}</p>
          </div>
        )}
      </Card>

      {/* 4. Ergebnis: Vertrag und Provision */}
      {istAbgeschlossen && (
        <Card className="border-l-4 border-l-emerald-500">
          <div className="flex items-start justify-between">
            <div>
              <Card.Title>Abschluss</Card.Title>
              <p className="text-sm text-gray-500 mt-0.5">
                Gewinner: <span className="font-medium text-gray-900">{gewinner ? gewinner.versorger_name : '–'}</span>
                {gewinner && toNum(gewinner.energiepreis_ct_kwh) !== null && (
                  <> · {fmtNum(gewinner.energiepreis_ct_kwh)} ct/kWh</>
                )}
              </p>
            </div>
            <Pill status={ausschreibung.provision_status} map={PROVISION_STATUS} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <Field label="Vertragsbeginn">{ausschreibung.vertragsbeginn ? formatDate(ausschreibung.vertragsbeginn) : '–'}</Field>
            <Field label="Vertragsende">{ausschreibung.vertragsende ? formatDate(ausschreibung.vertragsende) : '–'}</Field>
            <Field label="Laufzeit">
              {toNum(gewinner?.laufzeit_monate) === null ? '–' : `${toNum(gewinner.laufzeit_monate)} Monate`}
            </Field>
            <Field label="Provision">
              {toNum(ausschreibung.provision_ct_kwh) === null ? '–' : `${fmtNum(ausschreibung.provision_ct_kwh)} ct/kWh`}
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
              <p className="text-xs text-gray-500">Provision pro Jahr</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{fmtEur(provisionJahr)}</p>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3">
              <p className="text-xs text-emerald-700">Provision gesamt ueber die Laufzeit</p>
              <p className="text-2xl font-bold text-emerald-800 mt-1">{fmtEur(provisionGesamt)}</p>
            </div>
          </div>

          {/* Provisionsstatus fortschreiben */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Provisionsstatus</p>
            <div className="flex flex-wrap items-center gap-2">
              {Object.entries(PROVISION_STATUS).map(([value, cfg]) => {
                const active = (ausschreibung.provision_status || 'erwartet') === value;
                return (
                  <button
                    key={value}
                    onClick={() => handleProvisionStatus(value)}
                    disabled={active || !!provisionSaving}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:cursor-not-allowed ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50'
                    }`}
                  >
                    {provisionSaving === value ? 'Speichere …' : cfg.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-end gap-3 mt-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Zahldatum</label>
                <input
                  type="date"
                  value={zahldatum}
                  onChange={(e) => setZahldatum(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <Button variant="secondary" size="sm" icon={FiSave} onClick={handleZahldatum} loading={zahldatumSaving}>
                Speichern
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Verloren */}
      {istVerloren && (
        <Card className="border-l-4 border-l-red-500">
          <div className="flex items-start justify-between">
            <div>
              <Card.Title>Verloren</Card.Title>
              <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                {fmtText(ausschreibung.verloren_grund)}
              </p>
            </div>
            <Button variant="secondary" size="sm" icon={FiRefreshCw} onClick={handleWiederOeffnen}>
              Wieder oeffnen
            </Button>
          </div>
        </Card>
      )}

      {/* 2. Versorger-Rueckläufe */}
      <Card padding={false}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <Card.Title>Versorger-Rueckläufe</Card.Title>
            <p className="text-sm text-gray-500 mt-0.5">
              {angebote.length === 0
                ? 'Noch kein Versorger angefragt'
                : `${angebote.length} angefragt · ${eingegangen.length} eingegangen`}
            </p>
          </div>
          <Button
            variant="primary"
            className={BTN_PRIMARY}
            icon={FiPlus}
            onClick={() => { setAngebotError(''); setAngebotModal({ mode: 'anfrage', angebot: null }); }}
          >
            Versorger anfragen
          </Button>
        </div>

        {angebote.length === 0 ? (
          <div className="text-center py-12">
            <FiFileText className="mx-auto text-gray-400 mb-3" size={32} />
            <p className="text-gray-600 font-medium">Noch keine Anfrage erfasst</p>
            <p className="text-sm text-gray-500 mt-1">
              Legen Sie je angefragtem Versorger eine Zeile an und tragen Sie den Ruecklauf ein,
              sobald das Angebot vorliegt.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="px-6 py-3 font-medium">Versorger</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-3 py-3 font-medium text-right">Laufzeit</th>
                  <th className="px-3 py-3 font-medium text-right">Energiepreis</th>
                  <th className="px-3 py-3 font-medium text-right">Energiekosten/Jahr</th>
                  <th className="px-3 py-3 font-medium text-right">Grundpreis</th>
                  <th className="px-3 py-3 font-medium text-right">Leistungspreis</th>
                  <th className="px-3 py-3 font-medium">Bindefrist</th>
                  <th className="px-3 py-3 font-medium">Bemerkung</th>
                  <th className="px-6 py-3 font-medium text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortierteAngebote.map((a) => {
                  const istBestes = bestesAngebotId != null && String(a.id) === String(bestesAngebotId);
                  const istGewinner = gewinner != null && String(a.id) === String(gewinner.id);
                  // Stundengenau pruefen — ein Preis mit Bindefrist "heute 16:00"
                  // ist um 17:00 abgelaufen, auch wenn der Kalendertag stimmt.
                  const fristAbgelaufen = istFristAbgelaufen(a.bindefrist, a.bindefrist_uhrzeit);
                  const kosten = jahresEnergiekosten(a);
                  return (
                    <tr key={a.id} className={istBestes || istGewinner ? 'bg-emerald-50/60' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-3">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">{fmtText(a.versorger_name)}</span>
                          {istGewinner && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-600 text-white">
                              Gewinner
                            </span>
                          )}
                          {istBestes && !istGewinner && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                              Bestpreis
                            </span>
                          )}
                        </div>
                        {a.angefragt_am && (
                          <p className="text-xs text-gray-400 mt-0.5">angefragt {formatDate(a.angefragt_am)}</p>
                        )}
                      </td>
                      <td className="px-3 py-3"><Pill status={a.status} map={ANGEBOT_STATUS} /></td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        {toNum(a.laufzeit_monate) === null ? '–' : `${toNum(a.laufzeit_monate)} Mon.`}
                      </td>
                      <td className={`px-3 py-3 text-right whitespace-nowrap ${istBestes ? 'font-bold text-emerald-700' : 'font-medium text-gray-900'}`}>
                        {toNum(a.energiepreis_ct_kwh) === null ? '–' : `${fmtNum(a.energiepreis_ct_kwh)} ct`}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap text-gray-700">
                        {fmtEur(kosten)}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap text-gray-700">{fmtEur(a.grundpreis_eur_jahr)}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap text-gray-700">
                        {toNum(a.leistungspreis_eur_kw_jahr) === null ? '–' : `${fmtEur(a.leistungspreis_eur_kw_jahr)}/kW`}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {a.bindefrist ? (
                          <span className={fristAbgelaufen ? 'inline-flex items-center text-red-600 font-medium' : 'text-gray-700'}>
                            {fristAbgelaufen && <FiClock className="mr-1" size={13} />}
                            {formatDate(a.bindefrist)}
                          </span>
                        ) : '–'}
                        {fristAbgelaufen && <p className="text-xs text-red-600">abgelaufen</p>}
                      </td>
                      <td className="px-3 py-3 text-gray-600 max-w-[16rem]">
                        {(() => {
                          const kurz = tarifinfoZeilen(a.tarifinfo);
                          if (!a.bedingungen && kurz.length === 0) return '–';
                          return (
                            <div className="space-y-0.5">
                              {kurz.length > 0 && (
                                <span className="block text-xs text-gray-500">{kurz.join(' · ')}</span>
                              )}
                              {a.bedingungen && (
                                <span className="block line-clamp-2" title={a.bedingungen}>{a.bedingungen}</span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => { setAngebotError(''); setAngebotModal({ mode: 'ruecklauf', angebot: a }); }}
                            className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-gray-100"
                            title="Ruecklauf erfassen"
                          >
                            <FiEdit2 size={15} />
                          </button>
                          <button
                            onClick={() => { setDeleteError(''); setDeleteTarget(a); }}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                            title="Eintrag loeschen"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {ohneUhrzeit.length > 0 && (
          <p className="px-6 py-3 text-xs text-amber-700 border-t border-gray-100">
            {ohneUhrzeit.length === 1 ? 'Ein Angebot hat' : `${ohneUhrzeit.length} Angebote haben`} eine
            Bindefrist ohne Uhrzeit — solche Fristen gelten hier bis Tagesende. Versorger nennen
            die Uhrzeit meist mit; sie nachzutragen macht die Frist eindeutig.
          </p>
        )}

        {angebote.length > 0 && verbrauch === null && (
          <p className="px-6 py-3 text-xs text-amber-700 border-t border-gray-100">
            Fuer die Ausschreibung ist kein Verbrauch hinterlegt — die Jahres-Energiekosten
            koennen deshalb nicht berechnet werden.
          </p>
        )}
      </Card>

      {/* Vergleich mit dem Kunden teilen */}
      <Card>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FiShare2 size={18} className="text-teal-600" />
              Vergleich mit dem Kunden teilen
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Eine AIQCON-gebrandete Uebersicht der Rueckläufe — ohne Login aufrufbar.
            </p>
          </div>
        </div>

        {linkError && <div className="mb-4"><FormError message={linkError} /></div>}

        {!vergleichLink ? (
          <Button icon={FiLink} onClick={handleLinkErzeugen} loading={linkSaving}>
            {linkSaving ? 'Erzeuge Link …' : 'Vergleichslink erzeugen'}
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <input
                ref={linkInputRef}
                readOnly
                value={vergleichLink}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-[16rem] px-3 py-2 border rounded-lg bg-gray-50 text-sm font-mono"
              />
              <Button variant="secondary" icon={FiCopy} onClick={handleLinkKopieren}>
                {kopierStatus === 'ok' ? 'Kopiert!' : kopierStatus === 'manuell' ? 'Bitte kopieren' : 'Kopieren'}
              </Button>
              <a
                href={vergleichLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium text-gray-700"
              >
                <FiEye size={15} /> Ansehen
              </a>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-4">
              <button
                type="button"
                role="switch"
                aria-checked={!!ausschreibung.versorger_sichtbar}
                disabled={sichtbarSaving}
                onClick={() => handleSichtbarkeit(!ausschreibung.versorger_sichtbar)}
                className={`mt-0.5 relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                  ausschreibung.versorger_sichtbar ? 'bg-teal-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5 ${
                  ausschreibung.versorger_sichtbar ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
              <div>
                <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                  {ausschreibung.versorger_sichtbar
                    ? <><FiEye size={14} /> Versorgernamen sind sichtbar</>
                    : <><FiEyeOff size={14} /> Versorgernamen sind neutralisiert</>}
                </p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  {ausschreibung.versorger_sichtbar
                    ? 'Der Kunde sieht Versorger- und Produktnamen sowie den reinen Energiepreis.'
                    : 'Der Kunde sieht "Anbieter A", "Anbieter B" … — die Namen werden serverseitig zurueckgehalten, nicht nur ausgeblendet. Beim Erfassen des Abschlusses werden sie automatisch offengelegt.'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-gray-500">
              <FiInfo size={14} className="flex-shrink-0 mt-0.5" />
              <p>
                Beim Kunden erscheinen ausschliesslich Rueckläufe mit dem Status
                <strong> Angeboten</strong>. Angefragte, abgelehnte und abgelaufene Eintraege bleiben intern.
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Dialog: Versorger anfragen / Ruecklauf erfassen */}
      <Modal
        isOpen={!!angebotModal}
        onClose={closeAngebotModal}
        title={angebotModal?.mode === 'ruecklauf' ? 'Ruecklauf erfassen' : 'Versorger anfragen'}
        size="lg"
      >
        {angebotModal && (
          <AngebotForm
            key={angebotModal.angebot ? `angebot-${angebotModal.angebot.id}` : 'angebot-neu'}
            mode={angebotModal.mode}
            initialData={angebotModal.angebot}
            suppliers={suppliers}
            suppliersError={suppliersError}
            saving={angebotSaving}
            error={angebotError}
            onSave={handleAngebotSave}
            onCancel={closeAngebotModal}
          />
        )}
      </Modal>

      {/* Dialog: Ruecklauf loeschen */}
      <Modal isOpen={!!deleteTarget} onClose={closeDeleteModal} title="Eintrag loeschen?" size="sm">
        <p className="text-sm text-gray-600 mb-4">
          Soll die Anfrage an <strong>{deleteTarget?.versorger_name}</strong> mitsamt dem erfassten
          Ruecklauf geloescht werden? Diese Aktion kann nicht rueckgaengig gemacht werden.
        </p>
        {deleteError && <div className="mb-4"><FormError message={deleteError} /></div>}
        <div className="flex justify-end space-x-3">
          <Button variant="secondary" onClick={closeDeleteModal} disabled={deleting}>
            {deleteError ? 'Schliessen' : 'Abbrechen'}
          </Button>
          <Button variant="danger" icon={FiTrash2} onClick={handleAngebotDelete} loading={deleting}>
            {deleting ? 'Loesche …' : 'Loeschen'}
          </Button>
        </div>
      </Modal>

      {/* 3. Dialog: Abschluss erfassen */}
      <Modal isOpen={showAbschluss} onClose={closeAbschlussModal} title="Abschluss erfassen" size="lg">
        {showAbschluss && (
          eingegangen.length === 0 ? (
            <div className="text-center py-6">
              <FiAlertCircle className="mx-auto text-gray-400 mb-3" size={28} />
              <p className="text-sm text-gray-600">
                Es liegt noch kein Angebot mit dem Status "Angeboten" vor. Erfassen Sie zuerst
                mindestens einen Versorger-Ruecklauf.
              </p>
              <Button variant="secondary" className="mt-4" onClick={closeAbschlussModal}>Schliessen</Button>
            </div>
          ) : (
            <AbschlussForm
              ausschreibung={ausschreibung}
              angebote={eingegangen}
              saving={abschlussSaving}
              error={abschlussError}
              onSave={handleAbschluss}
              onCancel={closeAbschlussModal}
            />
          )
        )}
      </Modal>

      {/* Dialog: als verloren markieren */}
      <Modal isOpen={showVerloren} onClose={closeVerlorenModal} title="Als verloren markieren" size="sm">
        <form onSubmit={handleVerloren} className="space-y-4">
          <p className="text-sm text-gray-600">
            Warum ist die Ausschreibung nicht zustande gekommen? Der Grund hilft spaeter bei der
            Auswertung.
          </p>
          <FormError message={verlorenError} />
          <textarea
            value={verlorenGrund}
            onChange={(e) => setVerlorenGrund(e.target.value)}
            rows={3}
            className={inputClass}
            placeholder="z.B. Kunde hat beim Altversorger verlaengert"
            autoFocus
          />
          <div className="flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={closeVerlorenModal} disabled={verlorenSaving}>
              Abbrechen
            </Button>
            <Button type="submit" variant="danger" icon={FiXCircle} loading={verlorenSaving}>
              {verlorenSaving ? 'Speichere …' : 'Als verloren markieren'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AbschlussDetail;
